import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, Play, X } from "lucide-react";
import { MaskedLine } from "./Reveal";
import EngineStartButton from "./EngineStartButton";

const HERO_BG = "/media/hero.jpg";
const SHOWREEL_SRC = "/media/showreel.mp4";
const SHOWREEL_POSTER = "/media/showreel-poster.jpg";

function ShowreelModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="showreel-modal"
      className="fixed inset-0 z-[95] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="showreel-close-button"
        onClick={onClose}
        aria-label="Close showreel"
        className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <X size={20} />
      </button>

      <motion.div
        className="w-full max-w-5xl"
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">
          PURSUIT MEDIA STUDIOS — SHOWREEL
        </p>
        <video
          data-testid="showreel-video"
          src={SHOWREEL_SRC}
          poster={SHOWREEL_POSTER}
          controls
          autoPlay
          playsInline
          className="w-full max-h-[75vh] letterbox bg-black"
        />
        <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-smoke">
          COLIN WU MEDIA — DIRECTOR'S CUT
        </p>
      </motion.div>
    </motion.div>
  );
}

const LINES = [
  { text: "CHASING", outline: false },
  { text: "VELOCITY.", outline: true },
  { text: "CAPTURING", outline: false },
  { text: "RAW MOTION.", outline: false },
];

export default function Hero({ active, onNavigate }) {
  const ref = useRef(null);
  const [reelOpen, setReelOpen] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden flex flex-col justify-end"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_BG}
          alt="Race car on track at World Time Attack Challenge"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />
      </motion.div>

      {/* engine start button — floating centre-right on desktop */}
      <motion.div
        className="hidden lg:block absolute right-[9%] top-[44%] -translate-y-1/2 z-20"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={active ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.9, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ opacity: fade }}
      >
        <EngineStartButton size={150} onClick={() => onNavigate("#contact")} />
      </motion.div>

      <motion.div
        className="relative z-10 max-w-[1600px] w-full mx-auto px-6 md:px-12 pb-16 md:pb-24"
        style={{ opacity: fade, y: contentY }}
      >
        <MaskedLine active={active} delay={0.15}>
          <p className="font-mono text-[10px] md:text-xs tracking-[0.45em] text-white/70 mb-5 md:mb-8">
            AUTOMOTIVE &amp; MOTORSPORT — VIDEO / PHOTO / FILM
          </p>
        </MaskedLine>

        <h1
          data-testid="hero-kinetic-title"
          className="font-display text-white leading-[0.9] text-[clamp(3.4rem,11vw,10.5rem)]"
          style={{ fontWeight: 900 }}
        >
          {LINES.map((line, i) => (
            <MaskedLine key={line.text} active={active} delay={0.3 + i * 0.12}>
              <span className={line.outline ? "text-outline-signal" : ""}>{line.text}</span>
            </MaskedLine>
          ))}
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <MaskedLine active={active} delay={0.85}>
            <p className="max-w-md text-white/70 text-sm md:text-base font-light leading-relaxed">
              Pursuit Media Studios crafts cinema-grade films and imagery from the
              world of speed — pit lanes, apexes and everything in between.
            </p>
          </MaskedLine>

          <motion.div
            className="flex flex-col items-center gap-6 md:flex-row md:items-center"
            initial={{ opacity: 0, y: 24 }}
            animate={active ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="lg:hidden">
              <EngineStartButton size={96} testId="engine-start-button-mobile" onClick={() => onNavigate("#contact")} />
            </div>
            <div className="flex items-center gap-4">
              <button
                data-testid="hero-cta-button"
                onClick={() => onNavigate("#work")}
                className="group flex items-center gap-3 bg-signal text-white font-mono text-[11px] tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-ink transition-colors duration-300"
              >
                <Play size={13} className="fill-current" />
                VIEW OUR WORK
              </button>
              <button
                data-testid="hero-showreel-button"
                onClick={() => setReelOpen(true)}
                className="group flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] text-white/80 border border-white/25 px-7 py-4 hover:border-signal hover:text-signal transition-colors duration-300"
              >
                <span className="w-2 h-2 bg-signal rotate-45 group-hover:animate-pulse" />
                PLAY SHOWREEL
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50"
        initial={{ opacity: 0 }}
        animate={active ? { opacity: 1 } : {}}
        transition={{ delay: 1.6 }}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {reelOpen && <ShowreelModal onClose={() => setReelOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}
